import React, { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate, useSearchParams, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import DatePicker from "react-datepicker"
import Select from 'react-select'
import { Modal, Dropdown, DropdownButton } from 'react-bootstrap'

import ReactSelect from 'pages/parts/ReactSelect'
import DocumentTemplate from 'pages/parts/DocumentTemplate'
import usePreventExit from "pages/parts/usePreventExit"
import { httpOk, GET, POST, PUT, DELETE } from 'helpers/api'
import { handleDecimalKeyDown } from 'helpers/decimalKey'
import { formatDate, formatMoney, returnMinDate } from 'helpers/helpers'
import HintIcon from 'pages/parts/HintIcon'

function CreateUpdate() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const reduxAccount = useSelector(state => state.account)

	const [isDirty, setIsDirty] = useState(false);
	usePreventExit(isDirty);

	const tbodyRef = useRef()
	const scrollRef = useRef()

	const [data, setData] = useState({
		"products": [],
		// Точку передаём явно: без неё бэк берёт точку по умолчанию у пользователя,
		// и документ ложился не на тот филиал, который выбран в шапке кабинета
		"pos_id": reduxAccount.pos_id,
		"created_at": formatDate(new Date(), 'yyyy-MM-dd'),
	})
	const [products, setProducts] = useState([])
	// Ценовая категория документа: пусто — базовый прейскурант точки
	const [priceCategories, setPriceCategories] = useState([])
	const [productsDuplicate, setProductsDuplicate] = useState([])
	const [addQuantity, setAddQuantity] = useState(1)
	// Меню для быстрого заполнения: товары уже пришли с ценами, остаётся отобрать по разделу
	const [menus, setMenus] = useState([])
	const [modalMenu, setModalMenu] = useState({ 'bool': false, 'menu_id': null })
	// Шаблоны общие для документов: в них лежит только набор товаров
	const [modalTemplate, setModalTemplate] = useState({ 'bool': false, 'templates': [] })
	const [modalTemplateCRUD, setModalTemplateCRUD] = useState({ 'bool': false, 'name': "", 'products': [] })
	const [searchParams] = useSearchParams()

	async function createUpdate() {
		var dataCopy = { ...data }

		var response;
		if (dataCopy.id) {
			response = await PUT('/documents-price', dataCopy, { loader: true })
		} else {
			response = await POST('/documents-price', dataCopy, { loader: true })
		}

		// Снимаем «несохранённые данные» до перехода: иначе блокировщик покажет
		// confirm об уходе на уже сохранённом документе
		if (httpOk(response)) {
			flushSync(() => setIsDirty(false))
			navigate('/documents-price')
		}
	}

	function addToList(length = 1) {
		var dataCopy = JSON.parse(JSON.stringify(data))

		for (let i = 0; i < length; i++) {
			dataCopy.products.push({
				'product_id': '',
				'old_sale_price': '',
				'sale_price': '',
			})
		}

		setIsDirty(true)
		setData(dataCopy)

		setTimeout(() => {
			const byId = document.getElementById(`input-select-${dataCopy.products.length}`)
			byId?.select()
		}, 200);
	}

	// Все товары выбранного меню одной кнопкой: раньше их добавляли по одному
	// через селект, а в разделе их бывает несколько десятков.
	function fillByMenu(menuId) {
		if (!menuId) return

		var dataCopy = JSON.parse(JSON.stringify(data))
		var addedIds = dataCopy.products.map(item => item.product_id)

		for (const product of productsDuplicate) {
			if (product.menu_id !== menuId) continue
			if (addedIds.includes(product.id)) continue

			dataCopy.products.push({
				'product_id': product.id,
				'name': product.name,
				'old_sale_price': product.old_sale_price,
				'sale_price': product.sale_price,
			})
		}

		setIsDirty(true)
		setData(dataCopy)
		setModalMenu({ 'bool': false, 'menu_id': null })
	}

	// Подставляет товары одним списком: и по шаблону, и по меню строка выглядит
	// одинаково — товар, цена сейчас, предложенная новая цена
	function addProductsByIds(productIds) {
		var dataCopy = JSON.parse(JSON.stringify(data))
		var addedIds = dataCopy.products.map(item => item.product_id)

		for (const productId of productIds) {
			if (addedIds.includes(productId)) continue

			const product = productsDuplicate.find(item => item.id === productId)
			if (!product) continue

			dataCopy.products.push({
				'product_id': product.id,
				'name': product.name,
				'old_sale_price': product.old_sale_price,
				'sale_price': product.sale_price,
			})
		}

		setIsDirty(true)
		setData(dataCopy)
	}

	// ШАБЛОНЫ: таблица документов общая, в шаблоне хранится только набор товаров,
	// поэтому тот же шаблон годится и приходу, и изменению цен
	async function toggleModalTemplate(bool) {
		if (!bool) {
			setModalTemplate({ 'bool': false, 'templates': [] })
			return
		}

		const response = await GET('/documents-templates', {}, { loader: true })
		if (httpOk(response)) setModalTemplate({ 'bool': true, 'templates': response.data })
	}

	function toggleModalTemplateCRUD(bool) {
		setModalTemplateCRUD({ 'bool': bool, 'name': "", 'products': [] })
	}

	async function handleSelectTemplate(item) {
		const response = await GET(`/documents-templates-products/${item.id}`, {}, { loader: true })
		if (!httpOk(response)) return

		addProductsByIds((response.data ?? []).map(row => row.product_id))
		toggleModalTemplate(false)
	}

	async function deleteTemplate(item) {
		const response = await DELETE(`/documents-templates/${item.id}`, {}, { loader: true })
		if (httpOk(response)) toggleModalTemplate(true)
	}

	// Дубликат: те же товары, что и в выбранном документе, с их же новыми ценами.
	// Колонку «было» берём не из старого документа, а из текущего прейскуранта:
	// цена могла уже поменяться, и сравнивать надо с тем, что продаётся сейчас.
	async function copyFromDocument(id) {
		const response = await GET(`/documents-price/${id}`, {}, { loader: true })
		if (!httpOk(response)) return

		const source = response.data
		const priceCategoryId = source.price_category_id ?? null
		const catalog = await loadProducts(priceCategoryId)

		const products = (source.products ?? []).map(item => {
			const current = catalog.find(product => product.id === item.product_id)
			return {
				'product_id': item.product_id,
				'name': item.name,
				'old_sale_price': current ? current.old_sale_price : item.sale_price,
				'sale_price': item.sale_price,
			}
		})

		setData(prev => ({ ...prev, 'price_category_id': priceCategoryId, 'products': products }))
	}

	function deleteItem(index) {
		var dataCopy = JSON.parse(JSON.stringify(data))
		dataCopy.products.splice(index, 1)

		setData(dataCopy)
	}

	function handleInputChange(e, index) {
		var dataCopy = { ...data }

		dataCopy.products[index][e.target.name] = e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, '')

		setData(dataCopy)
	}

	// REACTSELECT LOGIC
	function handleSelectSearch(value) {
		if (value === '+' || value === '=') return
		if (!value || value === '+' || value === '=') {
			setProducts([...productsDuplicate])
			return
		}
		var productsDuplicateCopy = [...productsDuplicate]

		var filteredItems = productsDuplicateCopy.filter(item => item.name.toLowerCase().includes(value.toLowerCase()))
		if (filteredItems[0]) {
			filteredItems[0]['selected'] = true
		}
		setProducts(filteredItems)
	}

	function handleSelectProduct(option, index) {
		var dataCopy = { ...data }

		dataCopy.products[index]['name'] = option.name
		dataCopy.products[index]['product_id'] = option.id
		dataCopy.products[index]['old_sale_price'] = option.old_sale_price
		dataCopy.products[index]['sale_price'] = option.sale_price

		setData(dataCopy)
	}

	function closeReactSelect() {
		setTimeout(() => {
			var productsCopy = [...products]

			for (let i = 0; i < productsCopy.length; i++) productsCopy[i]['selected'] = false

			if (productsCopy[0] && productsCopy[0]['selected']) productsCopy[0]['selected'] = true

			setProducts(productsCopy)
		}, 100);
	}

	function handleTableKeyDown(event) {
		event.stopPropagation();
		var currentIndex = products.findIndex(el => el.selected);
		if (currentIndex >= 0) {
			var productsCopy = [...products]
			productsCopy[currentIndex].selected = false
			switch (event.key) {
				case "ArrowUp":
					if (currentIndex === 0) {
						productsCopy[products.length - 1].selected = true
						currentIndex = products.length - 1
					} else {
						productsCopy[currentIndex - 1].selected = true
					}
					scrollRef.current.scrollTop = (currentIndex * 29.6) - 59.2
					break;
				case "ArrowDown":
					if (currentIndex === products.length - 1) {
						productsCopy[0].selected = true
						currentIndex = 0
					} else {
						productsCopy[currentIndex + 1].selected = true
					}
					scrollRef.current.scrollTop = (currentIndex * 29.6)
					break;
				default: break;
			}

			setProducts(productsCopy)
		}
	}
	// REACTSELECT LOGIC

	async function loadProducts(priceCategoryId = null) {
		const params = { pos_id: reduxAccount.pos_id }
		if (priceCategoryId) params.price_category_id = priceCategoryId
		const response = await GET(`/documents-price-products`, params, { loader: true })

		if (!httpOk(response)) return []

		setProducts(response.data)
		setProductsDuplicate(response.data)
		return response.data
	}

	function getProducts(priceCategoryId = null) {
		loadProducts(priceCategoryId)
	}

	// Базовая цена — такой же пункт списка, а не пустое поле: пустым оно читалось бы
	// как «не выбрал», хотя документ в этом случае меняет базовый прейскурант точки.
	const priceCategoryOptions = [{ id: 0, name: t('base_price') }, ...priceCategories]

	async function getMenus() {
		const response = await GET('/helper/menu')
		if (httpOk(response)) setMenus(response.data ?? [])
	}

	async function getPriceCategories() {
		const response = await GET('/price-categories')
		if (httpOk(response)) setPriceCategories(response.data?.data ?? [])
	}

	// Смена категории меняет и колонку «было»: документ ведут по её ценам
	function selectPriceCategory(priceCategoryId) {
		setData({ ...data, 'price_category_id': priceCategoryId, 'products': [] })
		getProducts(priceCategoryId)
	}

	useEffect(() => {
		const copyId = searchParams.get('copy')
		// Дубликат сам грузит каталог по категории исходного документа
		if (copyId) copyFromDocument(copyId)
		else getProducts()

		getPriceCategories()
		getMenus()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('price_change')}
					</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{t('create')}</h6>
					</div>
				</div>
				<div className="d-flex gap-2">
					<DropdownButton title={t('action')}>
						<Dropdown.Item onClick={() => setModalMenu({ 'bool': true, 'menu_id': null })}>
							{t('fill_by_menu')}
						</Dropdown.Item>
						<hr className="m-0" />
						<Dropdown.Item onClick={() => toggleModalTemplate(true)}>
							{t('templates')}
						</Dropdown.Item>
						<Dropdown.Item onClick={() => toggleModalTemplateCRUD(true)}>
							{t('create_template')}
						</Dropdown.Item>
					</DropdownButton>
				</div>
			</div>

			<form autoComplete="off">
				<div className="card mb-3">
					<div className="card-body">
						<div className="row">
							<div className="col-md-2">
								<div className="form-group">
									<label>{t('date')}<span className="required-mark">*</span></label>
									<div className="calendar-input">
										<DatePicker
											showMonthDropdown
											showYearDropdown
											dropdownMode="select"
											yearDropdownItemNumber={2}
											className="form-control"
											dateFormat="dd.MM.yyyy"
											selected={data.created_at ? new Date(data.created_at) : ''}
											onChange={(date) => setData({ ...data, 'created_at': formatDate(date, 'yyyy-MM-dd') })}
											minDate={returnMinDate(new Date(), 20)}
											maxDate={new Date()} />
										<i className="uil uil-calendar-alt"></i>
									</div>
								</div>
							</div>
							<div className="col-md-2">
								<div className="form-group">
									<label>{t('note')}</label>
									<input type="text" className="form-control"
										name="note"
										onChange={(e) => setData({ ...data, 'note': e.target.value })} />
								</div>
							</div>
							{priceCategories.length > 0 &&
								<div className="col-md-3">
									<div className="form-group">
										<label>{t('price_category')}<HintIcon text={t('price_category_hint')} /></label>
										<Select
											options={priceCategoryOptions}
											value={priceCategoryOptions.find(option => option.id === (data.price_category_id ?? 0))}
											onChange={(option) => selectPriceCategory(option.id > 0 ? option.id : null)}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id} />
									</div>
								</div>
							}
						</div>
					</div>
				</div>

				<div className="card">
					<div className="card-body">
						<table className="table table-bordered" style={{ 'overflow': 'unset' }}>
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '30%' }}>{t('name2')}</th>
									<th style={{ 'width': '15%' }} className="text-end">{t('sale_price')}</th>
									<th style={{ 'width': '15%' }} className="text-end">{t('new_sale_price')}</th>
									<th style={{ 'width': '40%' }}></th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.products?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{index + 1}</td>
										<td>
											<ReactSelect
												uniqueId="select"
												search={handleSelectSearch}
												value={item.name}
												index={index}
												scrollRef={scrollRef}
												options={products}
												onClose={() => closeReactSelect()}
												selectItem={(option) => handleSelectProduct(option, index)}
												onKeyUp={(e) => {
													if (e.keyCode === 40 || e.keyCode === 38) {
														handleTableKeyDown(e)
													}
												}}
												table={
													<table className="table mb-0">
														<tbody ref={tbodyRef}>
															{products.map((selectItem, selectIndex) => (
																<tr
																	id={selectItem.id}
																	className={`cursor ${((selectItem.selected || selectItem.id === item.product_id) && 'selected')}`}
																	tabIndex={-1}
																	key={selectIndex}
																	onClick={() => handleSelectProduct(selectItem, index)}
																	onKeyDown={(e) => handleTableKeyDown(e, selectItem, index)}>
																	<td>{selectItem.name}</td>
																	<td>{selectItem.product_type_name}</td>
																	<td className="text-end text-nowrap">{formatMoney(selectItem.old_sale_price)}</td>
																</tr>
															))}
														</tbody>
													</table>
												}
											/>
										</td>
										<td className="text-end text-nowrap vertical-middle">
											{formatMoney(item.old_sale_price)}
										</td>
										<td className="td-input" style={!item.sale_price ? { 'borderBottom': '2px solid red' } : {}}>
											<input
												type="text"
												name="sale_price"
												placeholder="0"
												className="form-control text-end"
												value={item.sale_price ?? ''}
												onChange={(e) => handleInputChange(e, index)}
												onKeyDown={(e) => handleDecimalKeyDown(e, (event) => handleInputChange(event, index))}
											/>
										</td>
										<td></td>
										<td className="vertical-middle">
											<div className="d-flex justify-content-end">
												<div className="table-action-button table-action-danger-button"
													onClick={() => deleteItem(index)}>
													<i className="uil uil-times"></i>
												</div>
											</div>
										</td>
									</tr>
								))}
								<tr>
									<td className="td-input">
										<input
											type="text"
											className="form-control text-center"
											style={{ 'padding': '5px 0' }}
											placeholder="xxx"
											value={addQuantity}
											onChange={(e) => setAddQuantity(e.target.value.replace(/,/g, '.').replace(/[^0-9.]/g, ''))}
										/>
									</td>
									<td colSpan={1} style={{ 'padding': 0 }}>
										<div className="d-flex">
											<div className="w-100">
												<button type="button" className="btn-sm btn-primary w-100"
													onClick={() => {
														setAddQuantity(1)
														addToList(addQuantity)
													}}>
													{t('add')}
												</button>
											</div>
										</div>
									</td>
									<td></td>
									<td></td>
									<td></td>
									<td></td>
								</tr>
							</tbody>
						</table>

						<div className="btn-wrapper gap-2">
							<Link to="/documents-price" className="btn btn-warning btn-rounded btn-wide" tabIndex="-1">
								{t('cancel')}
							</Link>
							<button type="button" className="btn btn-primary btn-rounded btn-wide" tabIndex="-1"
								onClick={() => createUpdate(1)}>
								{t('confirm')}
							</button>
						</div>
					</div>
				</div>
			</form>

			{/* MODAL FILL BY MENU */}
			<Modal show={modalMenu.bool} animation={false} centered
				onHide={() => setModalMenu({ 'bool': false, 'menu_id': null })}>
				<Modal.Header closeButton>
					<Modal.Title>{t('fill_by_menu')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="form-group mb-0">
						<label>{t('menu')}</label>
						<Select
							options={menus}
							value={menus.find(option => option.id === modalMenu.menu_id) || ''}
							onChange={(option) => setModalMenu({ ...modalMenu, 'menu_id': option.id })}
							placeholder=""
							noOptionsMessage={() => t('list_empty')}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id} />
						<div className="fz-12 text-muted mt-2">{t('fill_by_menu_hint')}</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button type="button" className="btn btn-outline-warning w-100"
							onClick={() => setModalMenu({ 'bool': false, 'menu_id': null })}>
							{t('cancel')}
						</button>
						<button type="button" className="btn btn-primary w-100"
							disabled={!modalMenu.menu_id}
							onClick={() => fillByMenu(modalMenu.menu_id)}>
							{t('save')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>
			{/* MODAL FILL BY MENU */}

			{/* MODAL TEMPLATE */}
			<Modal show={modalTemplate.bool} animation={false} centered
				dialogClassName="update-modal-width" onHide={() => toggleModalTemplate(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('templates')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="table-responsive">
						<table className="table table-bordered table-hover cursor mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '100%' }}>{t('name2')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{modalTemplate?.templates?.map((item, index) => (
									<tr key={index}>
										<td className="text-center" style={{ 'minWidth': '33px' }}>{index + 1}</td>
										<td className="w-100"
											onClick={() => handleSelectTemplate(item)}>
											{item.name}
										</td>
										<td className="text-center" style={{ 'minWidth': '33px' }}>
											<div className="d-flex justify-content-end">
												<div className="table-action-button table-action-danger-button"
													onClick={() => deleteTemplate(item)}>
													<i className="uil uil-times"></i>
												</div>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL TEMPLATE */}

			{/* MODAL TEMPLATE CRUD */}
			{modalTemplateCRUD.bool &&
				<DocumentTemplate
					products={productsDuplicate}
					modalTemplateCRUD={modalTemplateCRUD}
					setModalTemplateCRUD={setModalTemplateCRUD}
					toggleModalTemplateCRUD={toggleModalTemplateCRUD}>
				</DocumentTemplate>
			}
			{/* MODAL TEMPLATE CRUD */}
		</>
	)
}

export default CreateUpdate